import { useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { useSnackbars } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { getApiClientErrorBody, getApiClientErrorStatus, getUserFacingErrorMessage } from '@/services/api/errors';
import { objectTypeKeys, updateObjectTypeSentAttributes, type ObjectType } from '@/services/db/object-type/entity';
import { useSafeNavigate } from '@/services/router/safe-navigate';

/** Shared by the object create and edit save flows. */
const SAVE_ERROR_SNACKBAR_ID = 'object.error';

const FieldErrorEnvelope = z.object({
  errors: z
    .array(z.looseObject({ detail: z.string().optional(), source: z.object({ pointer: z.string() }).optional() }))
    .nonempty(),
});

/** The attribute a 422 points at, or null when the body carries no usable pointer. */
export function firstFieldError(error: unknown): { field: string; detail: string } | null {
  const parsed = FieldErrorEnvelope.safeParse(getApiClientErrorBody(error));
  if (!parsed.success) return null;
  const [{ detail, source }] = parsed.data.errors;
  if (detail == null || source == null) return null;
  return { field: source.pointer.replace('/data/attributes/', ''), detail };
}

type ReportObjectSaveErrorOptions = {
  error: unknown;
  editor: ObjectAttributesEditorState;
  snackbars: ReturnType<typeof useSnackbars>;
  /** Names the action in the no-permission message. */
  verb: 'create' | 'edit';
};

/**
 * Surface a rejected object write: on the pointed-at row when the response
 * names one, otherwise as a critical snackbar.
 */
export function reportObjectSaveError({ error, editor, snackbars, verb }: ReportObjectSaveErrorOptions) {
  const fieldError = firstFieldError(error);
  // A field error is only "handled" when a row actually carries it; a 422
  // without a usable pointer still needs visible feedback.
  if (fieldError != null && editor.rows.some((row) => row.key === fieldError.field)) {
    editor.setServerError(fieldError.field, fieldError.detail);
    return;
  }
  const message =
    getApiClientErrorStatus(error) === 403
      ? `You do not have permission to ${verb} this object. Contact your Admin.`
      : getUserFacingErrorMessage(error, 'Something went wrong. Try again.');
  snackbars.create({ variant: 'critical', id: SAVE_ERROR_SNACKBAR_ID, message });
}

/**
 * Fold the attributes a write just sent into the type's `sent_attributes`, so
 * the Available Attributes panel lists them before the object API catches up.
 */
export function recordSentAttributes(
  queryClient: QueryClient,
  workspaceId: number,
  objectTypeId: string,
  savedAttributes: Readonly<Record<string, string>>,
) {
  queryClient.setQueryData<ObjectType[]>(objectTypeKeys.list(workspaceId), (types) =>
    types?.map((type) => (type.id === objectTypeId ? updateObjectTypeSentAttributes(type, savedAttributes) : type)),
  );
}

/**
 * Leaving an object editor: clear the edits and go, with dirtiness readable
 * through a ref. The leave guard's callback outlives a render, so the reset has
 * to be visible to a navigation fired in the same tick.
 */
export function useObjectSaveExit(editor: ObjectAttributesEditorState) {
  const safeNavigate = useSafeNavigate();
  const isDirtyRef = useRef(false);
  isDirtyRef.current = editor.isDirty;

  const resetAndNavigate = (href: string) => {
    editor.reset();
    isDirtyRef.current = false;
    safeNavigate(href);
  };

  return { resetAndNavigate, hasUnsavedChanges: () => isDirtyRef.current };
}
